import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import {
  QrCode, Search, Trash2, ArrowLeft, CheckCircle, AlertCircle,
  Users, Camera, CameraOff, Calendar
} from 'lucide-react';

const ROLE_LABEL = {
  membro: 'Membro', secretario: 'Secretário', tesoureiro: 'Tesoureiro',
  pastor: 'Pastor', administracao: 'Administração',
};
const ROLE_BG = {
  membro: '#64748b', secretario: '#3b82f6', tesoureiro: '#3b82f6',
  pastor: '#f59e0b', administracao: '#ef4444',
};

const fmtDate = (val) => {
  if (!val) return '';
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), "EEEE, dd 'de' MMMM", { locale: ptBR });
  return '';
};

const AvatarBubble = ({ user, size = 40 }) => {
  const API_STATIC = (process.env.NODE_ENV === 'development' && window.location.port === '3000')
    ? 'http://localhost:3001' : '';
  const src = user?.avatar ? `${API_STATIC}${user.avatar}` : null;
  const initials = (user?.name || '').split(' ').filter(Boolean).slice(0, 2).map(n => n[0].toUpperCase()).join('');
  const bg = ROLE_BG[user?.role] || '#64748b';
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', background: src ? 'transparent' : bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {src
        ? <img src={src} alt={user?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ color: '#fff', fontWeight: '700', fontSize: size * 0.35 }}>{initials}</span>
      }
    </div>
  );
};

const CheckIn = () => {
  const [step, setStep] = useState('select'); // 'select' | 'scan'
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type, user, message }
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const qrRef = useRef(null);
  const processingRef = useRef(false);
  const selectedEventRef = useRef(null);
  const feedbackTimer = useRef(null);

  useEffect(() => { selectedEventRef.current = selectedEvent; }, [selectedEvent]);

  // Carregar cultos
  useEffect(() => {
    api.get('/checkin/events').then(r => setEvents(r.data)).catch(() => {});
  }, []);

  // Carregar check-ins ao selecionar culto
  useEffect(() => {
    if (!selectedEvent) return;
    api.get(`/checkin/event/${selectedEvent._id}`).then(r => setCheckIns(r.data)).catch(() => {});
  }, [selectedEvent]);

  // Iniciar/parar scanner
  useEffect(() => {
    if (step !== 'scan' || !selectedEvent) return;
    setCamError(false);

    const qrCode = new Html5Qrcode('qr-reader-container');
    qrRef.current = qrCode;

    qrCode.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 220, height: 220 } },
      onScan,
      undefined
    )
      .then(() => setScanning(true))
      .catch(() => {
        setCamError(true);
        setScanning(false);
      });

    return () => {
      if (qrRef.current) {
        qrRef.current.stop().catch(() => {});
        qrRef.current = null;
      }
      setScanning(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedEvent?._id]);

  const onScan = useCallback(async (decoded) => {
    if (processingRef.current) return;
    const evt = selectedEventRef.current;
    if (!evt) return;
    processingRef.current = true;

    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);

    try {
      const res = await api.post('/checkin', { eventId: evt._id, userId: decoded.trim(), method: 'qr' });
      const { checkIn } = res.data;
      setCheckIns(prev => [checkIn, ...prev]);
      setFeedback({ type: 'success', user: checkIn.user, message: 'Check-in realizado!' });
    } catch (err) {
      if (err.response?.status === 409) {
        setFeedback({ type: 'duplicate', user: err.response.data.user, message: 'Já fez check-in' });
      } else if (err.response?.status === 404) {
        setFeedback({ type: 'error', message: 'QR Code não reconhecido' });
      } else {
        setFeedback({ type: 'error', message: 'Erro ao registrar' });
      }
    }

    feedbackTimer.current = setTimeout(() => {
      setFeedback(null);
      processingRef.current = false;
    }, 2500);
  }, []);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data.slice(0, 8));
    } catch {}
  };

  const handleManualCheckIn = async (member) => {
    try {
      const res = await api.post('/checkin', { eventId: selectedEvent._id, userId: member._id, method: 'manual' });
      const { checkIn } = res.data;
      setCheckIns(prev => [checkIn, ...prev]);
      toast.success(`✓ ${checkIn.user?.name || member.name}`);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      if (err.response?.status === 409) toast.warn('Membro já fez check-in');
      else toast.error('Erro ao registrar');
    }
  };

  const handleRemove = async (id) => {
    try {
      await api.delete(`/checkin/${id}`);
      setCheckIns(prev => prev.filter(c => c._id !== id));
    } catch {
      toast.error('Erro ao remover');
    }
  };

  const goBack = () => {
    setStep('select');
    setFeedback(null);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    processingRef.current = false;
  };

  const feedbackColors = {
    success: { bg: 'rgba(16,185,129,0.95)', icon: <CheckCircle size={32} color="#fff" /> },
    duplicate: { bg: 'rgba(245,158,11,0.95)', icon: <AlertCircle size={32} color="#fff" /> },
    error: { bg: 'rgba(239,68,68,0.95)', icon: <AlertCircle size={32} color="#fff" /> },
  };

  // ── Seleção de culto ────────────────────────────────────────────────────────
  if (step === 'select') {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <QrCode size={22} color="#3b82f6" />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>Check-in de Culto</h2>
          </div>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Selecione o culto para iniciar o check-in</p>
        </div>

        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8', fontSize: '14px' }}>
            Nenhum culto cadastrado ainda.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {events.map(evt => (
              <button
                key={evt._id}
                onClick={() => { setSelectedEvent(evt); setCheckIns([]); setStep('scan'); }}
                style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '14px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                onMouseOver={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; }}
              >
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Calendar size={20} color="#3b82f6" />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{evt.title || 'Culto'}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{fmtDate(evt.date)}{evt.time ? ` · ${evt.time}` : ''}</p>
                </div>
                {evt.presentTotal > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b' }}>
                    <Users size={13} /> {evt.presentTotal}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Tela de scan ────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: '860px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={goBack} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
          <ArrowLeft size={14} /> Voltar
        </button>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: '0 0 2px', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
            {selectedEvent?.title || 'Culto'}
          </h3>
          <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
            {fmtDate(selectedEvent?.date)}{selectedEvent?.time ? ` · ${selectedEvent.time}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '20px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <Users size={14} color="#16a34a" />
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#16a34a' }}>{checkIns.length}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* Coluna esquerda: scanner + busca manual */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Scanner */}
          <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', border: '1.5px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {scanning ? <Camera size={16} color="#3b82f6" /> : <CameraOff size={16} color="#94a3b8" />}
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Câmera</span>
              {scanning && <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#16a34a', fontWeight: '600' }}>● Ativa</span>}
            </div>

            {/* Área do scanner */}
            <div style={{ position: 'relative', background: '#0f172a' }}>
              <div id="qr-reader-container" style={{ width: '100%' }} />

              {/* Feedback overlay */}
              {feedback && (
                <div style={{ position: 'absolute', inset: 0, background: feedbackColors[feedback.type]?.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', zIndex: 10 }}>
                  {feedbackColors[feedback.type]?.icon}
                  {feedback.user && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <AvatarBubble user={feedback.user} size={44} />
                      <div style={{ textAlign: 'left' }}>
                        <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#fff' }}>{feedback.user.name}</p>
                        <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>{ROLE_LABEL[feedback.user.role] || feedback.user.role}</p>
                      </div>
                    </div>
                  )}
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#fff' }}>{feedback.message}</p>
                </div>
              )}

              {camError && (
                <div style={{ position: 'absolute', inset: 0, background: '#1e293b', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '24px' }}>
                  <CameraOff size={32} color="#64748b" />
                  <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>Câmera indisponível. Use a busca manual abaixo.</p>
                </div>
              )}
            </div>
          </div>

          {/* Busca manual */}
          <div style={{ background: '#fff', borderRadius: '14px', border: '1.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
            <button
              onClick={() => { setShowSearch(s => !s); setSearchQuery(''); setSearchResults([]); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' }}
            >
              <Search size={15} color="#3b82f6" />
              Busca manual
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#94a3b8' }}>{showSearch ? '▲' : '▼'}</span>
            </button>

            {showSearch && (
              <div style={{ padding: '0 14px 14px', borderTop: '1px solid #f1f5f9' }}>
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Nome do membro..."
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginTop: '12px' }}
                />
                {searchResults.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {searchResults.map(member => (
                      <button
                        key={member._id}
                        onClick={() => handleManualCheckIn(member)}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', textAlign: 'left' }}
                        onMouseOver={e => e.currentTarget.style.background = '#f0fdf4'}
                        onMouseOut={e => e.currentTarget.style.background = '#fff'}
                      >
                        <AvatarBubble user={member} size={32} />
                        <div>
                          <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{member.name}</p>
                          <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>{ROLE_LABEL[member.role] || member.role}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Coluna direita: lista de presentes */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '1.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', maxHeight: '620px' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={16} color="#3b82f6" />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Presentes</span>
            <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: '700', color: '#3b82f6', background: '#eff6ff', borderRadius: '12px', padding: '2px 10px' }}>{checkIns.length}</span>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
            {checkIns.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8', fontSize: '13px' }}>
                Nenhum check-in ainda
              </div>
            ) : (
              checkIns.map(ci => (
                <div
                  key={ci._id}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 8px', borderRadius: '10px', marginBottom: '4px', background: '#f8fafc' }}
                >
                  <AvatarBubble user={ci.user} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ci.user?.name}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>
                      {ci.method === 'manual' ? 'Manual' : 'QR'}
                      {ci.createdAt && ` · ${format(new Date(ci.createdAt), 'HH:mm')}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(ci._id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}
                    title="Remover check-in"
                    onMouseOver={e => e.currentTarget.style.color = '#ef4444'}
                    onMouseOut={e => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckIn;
